import { Injectable } from '@angular/core';
import 'moment-duration-format';
import { ISkill } from 'src/modules/shared/interfaces/skills.interface';
import { SkillsService } from './skills.service';
import { SkillInterface } from './../interfaces/skill.interface';
import { LocalStorageService } from './local-storage.service';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';

@Injectable()
export class SkillFilterationService {
    constructor(
        private skillsService: SkillsService,
        private localStorageService: LocalStorageService
    ) {}

    filterSkills(skills: ISkill[], skillsToKeep: string[]){
        return skills.filter(skill => skillsToKeep.indexOf(skill.name) > -1)
    }

    // //return an array against a skill to keep, passed from views 
    async getSkillsArray(skills: ISkill[], skillToKeep: string): Promise<ISkill[]> {
        const allSkills = await this.skillsService.getListOfAllSkills({outputFormat: 'default', filters: [skillToKeep]}) as string[];
        return this.filterSkills(skills, allSkills);
    }

    async doesUserHasHCSkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({industriesToInclude: ['Healthcare'], outputFormat: 'names'}) as string[];
        return this.filterSkills(skills, allSkills).length > 0
    }

    async doesUserHasHospitalitySkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({industriesToInclude: ['Hospitality'], outputFormat: 'names'}) as string[];
        return this.filterSkills(skills, allSkills).length > 0
    }

    async doesUserHasHCAttestationSkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({filters: ['isAttestationRequired'], outputFormat: 'names'}) as string[];
        return this.filterSkills(skills, allSkills).length > 0;
    }

    async doesUserHasBOHSkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({outputFormat: 'names', filters: ['isBOH']}) as string[];
        return this.filterSkills(skills, allSkills).length > 0;
    }

    async doesUserHasFOHSkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({outputFormat: 'names', filters: ['isFOH'],}) as string[];;
        return this.filterSkills(skills, allSkills).length > 0;
    }

    async doesUserHasGeneralSkills(skills: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({outputFormat: 'names', filters: ['isGeneral']}) as string[];;
        return this.filterSkills(skills, allSkills).length > 0;
    }

    async doesUserSkillRequireApparel(skills?: ISkill[]): Promise<boolean> {
        const allSkills = await this.skillsService.getListOfAllSkills({outputFormat: 'names', filters: ['isBOH', 'isFOH', 'isGeneral'],  }) as string[];
        return this.filterSkills(skills, allSkills).length > 0;
    }

    /**
     * Which industries' credentials should this contractor be shown?
     *
     * Skills are the source of truth once they are picked. Before that — the credentials step
     * can be reached with no skills on file — fall back to the path the contractor chose on the
     * first onboarding screen, the same order home.page and the shift filter modal use.
     * Both false means we could not tell, and callers show everything rather than an empty list.
     */
    async resolveCredentialIndustries(user: IUser): Promise<{isHCW: boolean, isHospitality: boolean}> {
        const skills = user && user.skills;

        if(skills && skills.length) {
            const [isHCW, isHospitality] = await Promise.all([
                this.doesUserHasHCSkills(skills),
                this.doesUserHasHospitalitySkills(skills)
            ]);

            if(isHCW || isHospitality) {
                return {isHCW, isHospitality};
            }
        }

        // The onboarding path. Prefer the copy on the user record (posted at signup by
        // contractor-information) over the local-storage one, which is device-local and gone
        // after a reinstall or on a second device.
        const expertise = (
            (user && user.expertise) || (await this.localStorageService.getExpertise()) || ''
        ).toString().toLowerCase();

        return {
            isHCW: expertise === 'healthcare',
            isHospitality: expertise === 'hospitality'
        };
    }

    async fetchSkillsWithRequiredCertificates(): Promise<SkillInterface[]> {
        const allSkills = await this.skillsService.getListOfAllSkills({industriesToInclude: ['Healthcare', 'Hospitality'], outputFormat: 'default'}) as SkillInterface[];
        return allSkills.filter((skill: SkillInterface) => skill.requiredCertificates.length > 0);
    }
}